'use client';

import { useState, useEffect } from 'react';
import { Star, Heart, ThumbsUp, Circle, MessageSquare, Send, RefreshCw, User, CheckCircle, Lock } from 'lucide-react';
import { useSession } from 'next-auth/react';
import { fetchAPI } from '@/core/api/client';
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import { Textarea } from '@/core/components/ui/textarea';
import { Label } from '@/core/components/ui/label';
import { toast } from '@/core/utils/toast';
import { cn } from '@/core/utils/cn';
import { useTranslation } from '@/core/components/providers/TranslationProvider';

interface Review {
    id: number;
    author_name: string;
    rating: number;
    comment: string | null;
    created_at: string;
}

export type RatingSymbol = 'star' | 'heart' | 'thumb' | 'circle';

export interface ReviewsSettings {
    layout?: 'list' | 'grid';
    ratingSymbol?: RatingSymbol;
    ratingColor?: string;
    cardBackground?: string;
    cardBorderColor?: string;
    cardBorderRadius?: number;
    accentColor?: string;
    showDivider?: boolean;
    buttonBg?: string;
    buttonTextColor?: string;
    buttonBorderRadius?: number;
}

function pluralizeReviewWord(count: number, locale: string): string {
    const n = Math.abs(count);
    switch (locale) {
        case 'pl': {
            if (n === 1) return 'opinia';
            const mod10 = n % 10, mod100 = n % 100;
            if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) return 'opinie';
            return 'opinii';
        }
        case 'de': return n === 1 ? 'Bewertung' : 'Bewertungen';
        case 'es': return n === 1 ? 'reseña' : 'reseñas';
        case 'fr': return 'avis';
        case 'cs': return 'hodnocení';
        case 'sk': {
            if (n === 1) return 'recenzia';
            const mod10 = n % 10, mod100 = n % 100;
            if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) return 'recenzie';
            return 'recenzií';
        }
        case 'it': return n === 1 ? 'recensione' : 'recensioni';
        case 'nl': return n === 1 ? 'beoordeling' : 'beoordelingen';
        case 'en':
        default: return n === 1 ? 'review' : 'reviews';
    }
}

/** Formats a review count with a correctly pluralized unit word for the given locale. */
export function formatReviewCount(count: number, locale: string): string {
    return `${count} ${pluralizeReviewWord(count, locale)}`;
}

export function RatingIcon({ symbol, filled, className, style }: { symbol: RatingSymbol; filled: boolean; className?: string; style?: React.CSSProperties }) {
    const cls = cn('transition-colors', className);
    switch (symbol) {
        case 'heart': return <Heart className={cls} fill={filled ? 'currentColor' : 'none'} style={style} />;
        case 'thumb': return <ThumbsUp className={cls} fill={filled ? 'currentColor' : 'none'} style={style} />;
        case 'circle': return <Circle className={cls} fill={filled ? 'currentColor' : 'none'} style={style} />;
        default: return <Star className={cls} fill={filled ? 'currentColor' : 'none'} style={style} />;
    }
}

export function RatingDisplay({
    value,
    max = 5,
    symbol = 'star',
    color = '#f59e0b',
    size = 'sm',
}: {
    value: number;
    max?: number;
    symbol?: RatingSymbol;
    color?: string;
    size?: 'sm' | 'md' | 'lg';
}) {
    const sizeClass = size === 'lg' ? 'w-6 h-6' : size === 'md' ? 'w-5 h-5' : 'w-3.5 h-3.5';
    return (
        <span className="flex items-center gap-0.5">
            {Array.from({ length: max }).map((_, i) => (
                <RatingIcon
                    key={i}
                    symbol={symbol}
                    filled={i < Math.round(value)}
                    className={cn(sizeClass, i < Math.round(value) ? '' : 'opacity-20')}
                    style={{ color: i < Math.round(value) ? color : undefined } as React.CSSProperties}
                />
            ))}
        </span>
    );
}

function RatingPicker({
    value,
    onChange,
    symbol = 'star',
    color = '#f59e0b',
}: {
    value: number;
    onChange: (v: number) => void;
    symbol?: RatingSymbol;
    color?: string;
}) {
    const [hover, setHover] = useState(0);
    return (
        <div className="flex items-center gap-1.5 h-11">
            {[1, 2, 3, 4, 5].map((n) => (
                <button
                    key={n}
                    type="button"
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => onChange(n)}
                    className="hover:scale-110 transition-transform"
                    style={{ color }}
                >
                    <RatingIcon symbol={symbol} filled={(hover || value) >= n} className="w-8 h-8" />
                </button>
            ))}
        </div>
    );
}

export function ProductReviews({
    productId,
    siteConfig,
    settings = {},
}: {
    productId: number;
    siteConfig: any;
    settings?: ReviewsSettings;
}) {
    const { t } = useTranslation();
    const {
        layout = 'list',
        ratingSymbol = 'star',
        ratingColor = '#f59e0b',
        cardBackground = '#ffffff',
        cardBorderColor = '#f1f5f9',
        cardBorderRadius = 16,
        accentColor,
        showDivider = true,
        buttonBg,
        buttonTextColor = '#ffffff',
        buttonBorderRadius = 12,
    } = settings;

    const btnStyle = buttonBg
        ? { backgroundColor: buttonBg, borderColor: buttonBg, color: buttonTextColor, borderRadius: buttonBorderRadius }
        : { borderRadius: buttonBorderRadius };

    const { data: session } = useSession();
    const isAdmin = session?.user?.role === 'admin';

    const [reviews, setReviews] = useState<Review[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const [rating, setRating] = useState(5);
    const [comment, setComment] = useState('');
    const [authorName, setAuthorName] = useState('');
    const [authorEmail, setAuthorEmail] = useState('');
    const [showForm, setShowForm] = useState(false);

    // Order verification — admins skip it
    const [orderNumber, setOrderNumber] = useState('');
    const [orderVerified, setOrderVerified] = useState(false);
    const [verifying, setVerifying] = useState(false);

    useEffect(() => {
        if (isAdmin) setOrderVerified(true);
    }, [isAdmin]);

    const loadReviews = async () => {
        try {
            const data = await fetchAPI(`/reviews/product/${productId}`);
            setReviews(Array.isArray(data) ? data : []);
        } catch {
            console.error('Failed to load reviews');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (siteConfig?.allow_reviews) loadReviews();
    }, [productId, siteConfig]);

    const handleVerifyOrder = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!orderNumber.trim()) { toast.error(t('reviews.error_order_number_required', 'Wpisz numer zamówienia')); return; }
        if (!session?.user && !authorEmail.trim()) { toast.error(t('reviews.error_email_required', 'Podaj adres e-mail użyty przy zamówieniu')); return; }
        setVerifying(true);
        try {
            const params = new URLSearchParams({ order_number: orderNumber.trim(), product_id: String(productId) });
            if (!session?.user) params.set('author_email', authorEmail.trim());
            const res = await fetchAPI(`/reviews/can-review?${params.toString()}`);
            if (res.allowed) {
                setOrderVerified(true);
                toast.success(t('reviews.order_verified', 'Zamówienie zweryfikowane — możesz dodać opinię'));
            } else {
                toast.error(res.reason || t('reviews.error_order_not_found', 'Nie znaleziono zamówienia z tym produktem'));
            }
        } catch {
            toast.error(t('reviews.error_verification', 'Błąd podczas weryfikacji'));
        } finally {
            setVerifying(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const trimmedComment = comment.trim() || null;
            const created = isAdmin
                ? await fetchAPI(`/reviews/admin/product/${productId}`, {
                    method: 'POST',
                    body: JSON.stringify({ rating, comment: trimmedComment, author_name: authorName, author_email: authorEmail }),
                })
                : await fetchAPI('/reviews', {
                    method: 'POST',
                    body: JSON.stringify({
                        product_id: productId,
                        order_number: orderNumber.trim(),
                        rating,
                        comment: trimmedComment,
                        author_name: authorName,
                        author_email: authorEmail,
                    }),
                });
            toast.success(created?.is_approved
                ? t('reviews.success_approved', 'Dziękujemy! Twoja opinia została dodana.')
                : t('reviews.success_pending', 'Dziękujemy! Opinia zostanie opublikowana po zatwierdzeniu.'));
            setComment(''); setRating(5); setShowForm(false);
            if (created?.is_approved) loadReviews();
        } catch (err: any) {
            toast.error(err?.message || t('reviews.error_submit', 'Wystąpił błąd podczas dodawania opinii'));
        } finally {
            setSubmitting(false);
        }
    };

    if (!siteConfig?.allow_reviews) return null;

    const averageRating = reviews.length > 0
        ? reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length
        : 0;

    const avatarBg = accentColor ? `${accentColor}20` : undefined;
    const avatarColor = accentColor || undefined;

    return (
        <div className={cn('space-y-10', showDivider && 'mt-16 pt-16 border-t border-slate-100')}>
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                <div className="space-y-2">
                    <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-3">
                        <MessageSquare className="w-8 h-8" style={accentColor ? { color: accentColor } : undefined} />
                        {t('reviews.title', 'Opinie klientów')}
                    </h2>
                    <div className="flex items-center gap-4">
                        <RatingDisplay value={averageRating} symbol={ratingSymbol} color={ratingColor} size="md" />
                        <span className="text-sm font-bold text-slate-500">
                            {reviews.length > 0
                                ? t('reviews.average', 'Średnia {avg} / 5 ({count} opinii)')
                                    .replace('{avg}', averageRating.toFixed(1))
                                    .replace('{count}', String(reviews.length))
                                : t('reviews.no_reviews', 'Brak opinii')}
                        </span>
                    </div>
                </div>
                <Button
                    onClick={() => setShowForm(!showForm)}
                    variant={showForm ? 'outline' : 'default'}
                    className="rounded-xl font-bold px-6 h-12 shadow-lg hover:translate-y-[-2px] transition-all"
                    style={showForm ? undefined : btnStyle}
                >
                    {!orderVerified && !showForm && <Lock className="w-4 h-4 mr-2" />}
                    {showForm ? t('reviews.cancel', 'Anuluj') : orderVerified ? t('reviews.add', 'Dodaj swoją opinię') : t('reviews.add_anon', 'Dodaj opinię')}
                </Button>
            </div>

            {/* Order verification */}
            {showForm && !orderVerified && (
                <form onSubmit={handleVerifyOrder} className="bg-slate-50 rounded-2xl p-6 md:p-8 border border-slate-200 shadow-sm space-y-4 animate-in fade-in slide-in-from-top-4 duration-300">
                    <div className="flex items-center gap-2 text-slate-700">
                        <Lock className="w-4 h-4" />
                        <p className="text-sm font-bold">{t('reviews.verify_order', 'Numer zamówienia')}</p>
                    </div>
                    <p className="text-sm text-slate-500">{t('reviews.verify_desc', 'Podaj numer zamówienia, aby potwierdzić zakup i dodać opinię.')}</p>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <Input
                            placeholder={t('reviews.verify_order', 'Numer zamówienia np. 2025/0042')}
                            value={orderNumber}
                            onChange={e => setOrderNumber(e.target.value)}
                            className="flex-1"
                        />
                        {!session?.user && (
                            <Input
                                type="email"
                                placeholder={t('reviews.verify_email', 'E-mail użyty przy zamówieniu')}
                                value={authorEmail}
                                onChange={e => setAuthorEmail(e.target.value)}
                                className="flex-1"
                            />
                        )}
                        <Button type="submit" disabled={verifying} className="font-bold gap-2 shrink-0" style={btnStyle}>
                            {verifying ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
                            {verifying ? t('reviews.verifying', 'Weryfikowanie...') : t('reviews.verify_btn', 'Zweryfikuj zamówienie')}
                        </Button>
                    </div>
                </form>
            )}

            {/* Form */}
            {showForm && orderVerified && (
                <form onSubmit={handleSubmit} className="bg-slate-50 rounded-2xl p-6 md:p-8 border border-slate-200 shadow-sm space-y-6 animate-in fade-in slide-in-from-top-4 duration-300">
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider">{t('reviews.name', 'Twoje imię')}</Label>
                            <Input placeholder={t('reviews.name', 'Twoje imię')} value={authorName} onChange={e => setAuthorName(e.target.value)} maxLength={30} className="bg-white border-slate-200 rounded-lg h-11" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider">{t('reviews.name', 'Ocena')}</Label>
                            <RatingPicker value={rating} onChange={setRating} symbol={ratingSymbol} color={ratingColor} />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider">{t('reviews.comment', 'Treść opinii')}</Label>
                        <Textarea placeholder={t('reviews.comment', 'Napisz co sądzisz o produkcie...')} rows={4} value={comment} onChange={e => setComment(e.target.value)} maxLength={2000} className="bg-white border-slate-200 rounded-lg resize-none" />
                    </div>
                    <div className="flex justify-end">
                        <Button disabled={submitting} className="font-bold px-8 h-11 gap-2" style={btnStyle}>
                            {submitting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            {submitting ? t('reviews.submitting', 'Wysyłanie...') : t('reviews.submit', 'Wyślij opinię')}
                        </Button>
                    </div>
                </form>
            )}

            {/* Reviews list */}
            <div className="space-y-6">
                {loading ? (
                    <div className="flex items-center justify-center p-12 text-slate-400 gap-2">
                        <RefreshCw className="w-5 h-5 animate-spin" /> Ładowanie opinii...
                    </div>
                ) : reviews.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-12 text-center space-y-3">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-50 rounded-full">
                            <RatingIcon symbol={ratingSymbol} filled={false} className="w-8 h-8 text-slate-200" />
                        </div>
                        <p className="text-slate-500 font-medium">{t('reviews.be_first', 'Bądź pierwszym, który oceni ten produkt!')}</p>
                    </div>
                ) : (
                    <div className={cn('grid gap-6', layout === 'grid' ? 'md:grid-cols-2' : 'grid-cols-1')}>
                        {reviews.map((review) => (
                            <div
                                key={review.id}
                                className="p-6 shadow-sm hover:shadow-md transition-shadow duration-300"
                                style={{
                                    background: cardBackground,
                                    border: `1px solid ${cardBorderColor}`,
                                    borderRadius: cardBorderRadius,
                                }}
                            >
                                <div className="flex flex-wrap justify-between items-start gap-x-4 gap-y-2">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: avatarBg || '#f1f5f9' }}>
                                            <User className="w-5 h-5" style={{ color: avatarColor || '#64748b' }} />
                                        </div>
                                        <p className="font-bold text-slate-900 truncate">{review.author_name || 'Anonimowy klient'}</p>
                                    </div>
                                    <RatingDisplay value={review.rating} symbol={ratingSymbol} color={ratingColor} size="sm" />
                                </div>
                                {review.comment && (
                                    <div className="mt-4 text-slate-600 leading-relaxed italic">
                                        "{review.comment}"
                                    </div>
                                )}
                                <div className="mt-4 flex items-center gap-1.5 text-[10px] text-green-600 font-bold uppercase tracking-widest">
                                    <CheckCircle className="w-3 h-3" /> Zweryfikowany zakup
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
